import * as PopoverPrimitive from "@radix-ui/react-popover";
import { ChevronLeft, ChevronRight, Compass, X } from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";
import { useLocation, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { useAuth } from "@/lib/auth";
import { useSaveUiPrefs, useUiPrefs } from "@/lib/queries";
import {
  TOUR_SCROLL_MARGIN, TOUR_WAIT_MS, findTarget, modalOpen, needsScroll, placeWords, sameBox,
  shouldOfferTour, tallerThanView, tourPrefKey, tourRecord, tourSteps, waitForTarget, withSearch,
  type Box, type Tour, type TourOutcome, type TourStep,
} from "@/lib/tours";
import { cn } from "@/lib/utils";
import { useSoftphone } from "@/softphone/softphone-context";

/**
 * The product tour (ACL-332): a screen walks the person through itself one element at a time.
 *
 * A screen declares its tour as a list of steps beside its code and calls `useTour(tour, {
 * ready })`; each step names an element by its `data-tour` attribute. The rules — who gets which
 * step, when a tour starts on its own, what is remembered — are `lib/tours.ts`. This file draws
 * it: a tint over the page with the step's element cut out of it, and the step's words in a
 * popover beside the element, with Back, Next, Skip and a count.
 *
 * Three things about the drawing are not guessable:
 *
 *   - The tint covers the PAGE (`<main>`), not the window. The phone dock and the sidebar stay
 *     bright and usable, because a tour must never sit over Answer; a call that rings or is up
 *     ends the tour anyway, without recording it as taken, and so does leaving the page. The
 *     layers follow from that: the tint sits over the page's own content (which stops at z-20)
 *     and under the phone where it floats on a small screen (z-40); the words sit with the
 *     console's other popovers (z-50), so a dialog or a menu opened after them covers them.
 *   - The cut-out is one element with a box-shadow the size of the viewport, inside a frame that
 *     clips to the page: that gives rounded corners and a tint confined to the page in one
 *     element. The popover is anchored to a box over it, so Radix places and keeps the words
 *     beside the element while the page scrolls under the tour. Which side is `placeWords`'s
 *     answer, from the room the page has: Radix alone only flips to the opposite side.
 *   - The page is blocked while the tour is up (the frame takes every pointer event; the wheel is
 *     passed on so the page still scrolls), and the popover keeps the keyboard: Enter, the
 *     arrows and Escape move the tour, Tab stays inside the popover.
 */

const PAD = 6;

interface TourContextValue {
  /** The tour of the screen on show, for "Take the tour" in the page header. */
  pageTour: Tour | null;
  setPageTour: (tour: Tour | null) => void;
  /** Start a tour now: false when none of its steps has an element on the page. */
  start: (tour: Tour) => boolean;
}

const TourContext = React.createContext<TourContextValue | null>(null);

interface ActiveTour {
  tour: Tour;
  steps: TourStep[];
  index: number;
  /** Where the tour started, to end it when the person leaves and to put the URL back. */
  path: string;
  search: string;
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const [pageTour, setPageTour] = React.useState<Tour | null>(null);
  const [active, setActive] = React.useState<ActiveTour | null>(null);
  const role = user?.role;

  const start = React.useCallback((tour: Tour): boolean => {
    // A step whose element is not on the page now is left out, so the count is right. A step on
    // another tab (`search`) cannot be checked yet; it is skipped later if its element never comes.
    const steps = tourSteps(tour, role).filter((step) => step.search || findTarget(step.target));
    if (steps.length === 0) return false;
    setActive({ tour, steps, index: 0, path: location.pathname, search: location.search });
    return true;
  }, [role, location.pathname, location.search]);

  const value = React.useMemo(() => ({ pageTour, setPageTour, start }), [pageTour, start]);

  return (
    <TourContext.Provider value={value}>
      {children}
      <TourLayer active={active} setActive={setActive} />
    </TourContext.Provider>
  );
}

/**
 * A screen's tour. Registers it for the page header's button, and starts it on its own the first
 * time the person opens the screen — once `ready`, which the screen sets when the elements the
 * steps point at are on the page (its data has loaded). The decision is made once per visit: a
 * screen opened during a call does not start its tour when the call ends. A dialog open at that
 * moment only delays it: the tour starts when the dialog closes.
 *
 * A screen with more than one state passes the tour of the state it is in (a new cadence: the
 * shapes to start from, then the plan), and each tour gets its own decision, by its id. A tour
 * built from what the screen shows (a campaign's tabs) keeps its id, so a rebuilt one is not
 * offered twice.
 */
export function useTour(tour: Tour, { ready = true }: { ready?: boolean } = {}): void {
  const context = React.useContext(TourContext);
  const { user } = useAuth();
  const phone = useSoftphone();
  const prefs = useUiPrefs();
  const onCall = phone.calls.some((call) => call.state !== "ended");
  const modal = useModalOpen();
  const decided = React.useRef(new Set<string>());

  React.useEffect(() => {
    if (!context) return;
    context.setPageTour(tour);
    return () => context.setPageTour(null);
  }, [context, tour]);

  React.useEffect(() => {
    if (!context || decided.current.has(tour.id) || !ready || prefs.isPending || modal) return;
    decided.current.add(tour.id);
    const offer = shouldOfferTour(tour, {
      role: user?.role, prefs: prefs.data, onCall, signedInAs: Boolean(user?.signed_in_by),
    });
    if (offer) context.start(tour);
  }, [context, tour, ready, prefs.isPending, prefs.data, user, onCall, modal]);
}

/** `modalOpen`, kept current: Radix writes the body's style as a modal layer opens and closes. */
function useModalOpen(): boolean {
  const [open, setOpen] = React.useState(() => modalOpen());
  React.useEffect(() => {
    const read = () => setOpen(modalOpen());
    const observer = new MutationObserver(read);
    observer.observe(document.body, { attributes: true, attributeFilter: ["style"] });
    read();
    return () => observer.disconnect();
  }, []);
  return open;
}

/** "Take the tour", for the screen on show. `PageHeader` draws it; a screen with a header of its
 *  own draws it itself. Nothing when the screen has no tour, or none of it is for this role. */
export function TourButton({ className }: { className?: string }) {
  const context = React.useContext(TourContext);
  const { user } = useAuth();
  const toast = useToast();
  const tour = context?.pageTour ?? null;
  if (!context || !tour || tourSteps(tour, user?.role).length === 0) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      className={className}
      // The label gives way on a narrow screen, where it would take the title's room.
      aria-label="Take the tour"
      onClick={() => {
        if (context.start(tour)) return;
        toast.info("Nothing to show yet", "This page has none of the things the tour points at.");
      }}
    >
      <Compass />
      <span className="hidden sm:inline">Take the tour</span>
    </Button>
  );
}

function viewportBox(): Box {
  return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
}

function boxOf(element: Element): Box {
  const rect = element.getBoundingClientRect();
  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
}

/** Scroll a step's element into view when it is not: to the middle of the page, or, for one too
 *  tall to show whole, with its top just under the page's top edge. */
function bringIntoView(element: Element): void {
  const box = boxOf(element);
  const view = viewportBox();
  if (!needsScroll(box, view)) return;
  if (!tallerThanView(box, view)) {
    element.scrollIntoView({ block: "center", inline: "nearest" });
    return;
  }
  element.scrollIntoView({ block: "start", inline: "nearest" });
  document.querySelector("main")?.scrollBy({ top: -TOUR_SCROLL_MARGIN });
}

function TourLayer({
  active, setActive,
}: {
  active: ActiveTour | null;
  setActive: React.Dispatch<React.SetStateAction<ActiveTour | null>>;
}) {
  const { user } = useAuth();
  const phone = useSoftphone();
  const location = useLocation();
  const [, setParams] = useSearchParams();
  const { mutate: savePrefs } = useSaveUiPrefs();
  const onCall = phone.calls.some((call) => call.state !== "ended");

  // The step on show, with its element. It trails `active.index` while the next step's element is
  // being found (a tab opening), so the words never sit beside the element of another step, and
  // the popover stays mounted from step to step: no flicker, and the keyboard stays in it.
  const [shown, setShown] = React.useState<{ index: number; target: Element } | null>(null);
  const [box, setBox] = React.useState<Box | null>(null);
  const [frame, setFrame] = React.useState<Box | null>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const target = shown?.target ?? null;
  const titleId = React.useId();
  const bodyId = React.useId();

  // What the effects and the loop read, without restarting on every render.
  const activeRef = React.useRef(active);
  activeRef.current = active;
  const setParamsRef = React.useRef(setParams);
  setParamsRef.current = setParams;

  const step = active && shown ? active.steps[shown.index] : undefined;
  const last = active && shown ? shown.index === active.steps.length - 1 : false;
  /** The next step's element is still being found: the buttons wait, so no step is jumped unseen. */
  const pending = Boolean(active && shown && shown.index !== active.index);
  const pendingRef = React.useRef(pending);
  pendingRef.current = pending;

  const end = React.useCallback((outcome: TourOutcome | null) => {
    const current = activeRef.current;
    if (!current) return;
    setActive(null);
    if (!outcome) return;
    // Recorded on the account, never while an admin is signed in as the person (ADR-040).
    if (user && !user.signed_in_by) {
      savePrefs({ [tourPrefKey(current.tour.id)]: tourRecord(current.tour, outcome) });
    }
    if (current.steps.some((s) => s.search) && window.location.search !== current.search) {
      setParamsRef.current(new URLSearchParams(current.search), { replace: true });
    }
  }, [setActive, user, savePrefs]);

  /** On to the next step, or the tour is over. Also what a step does whose element never came, or
   *  went away under the tour. */
  const advance = React.useCallback(() => {
    const current = activeRef.current;
    if (!current) return;
    if (current.index + 1 >= current.steps.length) end("finished");
    else setActive({ ...current, index: current.index + 1 });
  }, [setActive, end]);
  const advanceRef = React.useRef(advance);
  advanceRef.current = advance;

  const next = React.useCallback(() => {
    if (!pendingRef.current) advance();
  }, [advance]);

  const back = React.useCallback(() => {
    const current = activeRef.current;
    if (!current || pendingRef.current) return;
    if (current.index > 0) setActive({ ...current, index: current.index - 1 });
  }, [setActive]);

  // Leaving the page or taking a call ends the tour, and neither counts as having taken it.
  React.useEffect(() => {
    if (active && active.path !== location.pathname) end(null);
  }, [active, location.pathname, end]);
  React.useEffect(() => {
    if (active && onCall) end(null);
  }, [active, onCall, end]);

  // Each step: open its tab if it has one, find its element, bring it into view.
  const tourId = active?.tour.id;
  const index = active?.index;
  React.useEffect(() => {
    const current = activeRef.current;
    const at = current?.steps[current.index];
    if (!current || !at) return;
    const controller = new AbortController();
    const stepIndex = current.index;
    if (at.search) {
      const now = new URLSearchParams(window.location.search);
      const wanted = withSearch(now, at.search);
      if (wanted.toString() !== now.toString()) setParamsRef.current(wanted, { replace: true });
    }
    void waitForTarget(at.target, TOUR_WAIT_MS, controller.signal).then((element) => {
      if (controller.signal.aborted) return;
      if (!element) {
        advanceRef.current();
        return;
      }
      bringIntoView(element);
      setShown({ index: stepIndex, target: element });
    });
    return () => controller.abort();
  }, [tourId, index]);

  // Nothing is left on show once the tour is over.
  React.useEffect(() => {
    if (tourId !== undefined) return;
    setShown(null);
    setBox(null);
  }, [tourId]);

  // Follow the element while the page scrolls or re-lays out, and the page's own frame while
  // the sidebar folds. One measurement a frame, and a render only when something moved.
  React.useEffect(() => {
    if (!target) return;
    const main = document.querySelector("main");
    let handle = 0;
    let lastBox: Box | null = null;
    let lastFrame: Box | null = null;
    const tick = () => {
      if (!target.isConnected || target.getClientRects().length === 0) {
        // Gone because the next step opened another tab: that step is already being found, and
        // the box stays where it was until it is.
        if (pendingRef.current) return;
        // Redrawn under the tour (a list refetched), or hidden by a page that changed width: the
        // element that carries the name now, or move on.
        const current = activeRef.current;
        const at = current?.steps[current.index];
        const again = at ? findTarget(at.target) : null;
        if (current && again) setShown({ index: current.index, target: again });
        else advanceRef.current();
        return;
      }
      const nextBox = boxOf(target);
      if (!sameBox(nextBox, lastBox)) {
        lastBox = nextBox;
        setBox(nextBox);
      }
      const nextFrame = main ? boxOf(main) : viewportBox();
      if (!sameBox(nextFrame, lastFrame)) {
        lastFrame = nextFrame;
        setFrame(nextFrame);
      }
      handle = requestAnimationFrame(tick);
    };
    handle = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(handle);
  }, [target]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      next();
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      back();
    } else if (event.key === "Enter" && event.target === event.currentTarget) {
      event.preventDefault();
      next();
    } else if (event.key === "Tab") {
      // The popover keeps the keyboard: the page under the tint is not for using while it is up.
      const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLElement>("button:not([disabled])"));
      const first = buttons[0];
      const lastButton = buttons[buttons.length - 1];
      if (!first || !lastButton) return;
      const focused = document.activeElement;
      if (event.shiftKey && (focused === first || focused === event.currentTarget)) {
        event.preventDefault();
        lastButton.focus();
      } else if (!event.shiftKey && focused === lastButton) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  if (!active || !shown || !step || !box || !frame) return null;

  const spot = {
    top: box.top - frame.top - PAD,
    left: box.left - frame.left - PAD,
    width: box.width + PAD * 2,
    height: box.height + PAD * 2,
  };
  // Where the words hang: beside the cut-out, or, for an element with no room on any side, from
  // a line across its top, which puts them inside it and on screen.
  const place = placeWords(step.side ?? "bottom", box, frame);
  const anchor = place.inside
    ? { ...spot, top: Math.max(spot.top, TOUR_SCROLL_MARGIN - PAD), height: 0 }
    : spot;

  return createPortal(
    <PopoverPrimitive.Root open modal={false}>
      <div
        aria-hidden
        className="fixed z-30 overflow-hidden"
        style={{ top: frame.top, left: frame.left, width: frame.width, height: frame.height }}
        onWheel={(event) => {
          document.querySelector("main")?.scrollBy({ left: event.deltaX, top: event.deltaY });
        }}
      >
        <div
          className={cn(
            "absolute rounded-lg ring-2 ring-accent-9 shadow-[0_0_0_200vmax_var(--color-scrim)]",
            "transition-[top,left,width,height] duration-200 ease-out",
          )}
          style={spot}
        />
        <PopoverPrimitive.Anchor asChild>
          <div className="absolute transition-[top,left,width,height] duration-200 ease-out" style={anchor} />
        </PopoverPrimitive.Anchor>
      </div>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={contentRef}
          tabIndex={-1}
          side={place.side}
          align={step.align ?? "start"}
          sideOffset={12}
          collisionPadding={12}
          updatePositionStrategy="always"
          aria-labelledby={titleId}
          aria-describedby={bodyId}
          onOpenAutoFocus={(event) => {
            // The words take the keyboard as the tour opens, without the page scrolling to them.
            event.preventDefault();
            contentRef.current?.focus({ preventScroll: true });
          }}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            end("skipped");
          }}
          onKeyDown={onKeyDown}
          className={cn(
            "z-50 w-72 rounded-lg border border-border bg-surface-raised p-3 shadow-lg outline-none",
            "animate-overlay-in",
          )}
        >
          <div className="flex items-start justify-between gap-2">
            <p id={titleId} aria-live="polite" className="text-sm font-semibold text-text">
              {step.title}
            </p>
            <Button
              variant="ghost"
              size="icon-sm"
              className="-mr-1.5 -mt-1.5"
              aria-label="Skip the tour"
              onClick={() => end("skipped")}
            >
              <X />
            </Button>
          </div>
          <p id={bodyId} aria-live="polite" className="mt-1 text-sm leading-relaxed text-text-muted">
            {step.body}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-text-subtle">
              {shown.index + 1} of {active.steps.length}
            </span>
            <div className="ml-auto flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={back} disabled={pending || shown.index === 0}>
                <ChevronLeft />
                Back
              </Button>
              <Button variant="default" size="sm" onClick={next} disabled={pending}>
                {last ? "Done" : "Next"}
                {!last && <ChevronRight />}
              </Button>
            </div>
          </div>
          <PopoverPrimitive.Arrow className="fill-surface-raised" width={14} height={7} />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>,
    document.body,
  );
}
